import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';

import RouterLink from 'src/components/ui/RouterLink';
import FilterPlateCDefault from 'src/components/ui/FilterPlate/connectors/FilterPlateCDefault';
import { VALUE_TYPE } from 'src/components/ui/FilterPlate/constants';
import { CELL_RENDER_TYPE } from 'src/components/ui/CellRender/constants';

import DefaultTable from '..';
import { COLUIMN_MIN_WIDTH } from '../constants';
import { useFilters } from 'src/hooks/filters/useFilters';
import {
	indexColumnsByCode,
	mapSelectedFilters,
} from 'src/hooks/filters/mapSelectedFilters';
import {
	GORKO_STATUS_FILTER_OPTIONS,
	getGorkoStatusFilterLabel,
	mapCompanyGorkoStatus,
} from 'src/utils/mappers/companyGorkoStatus.mapper';

const defultLinkGenerator = ({ id }) => `/company/${id}`;

const DefaultTableCCalls = (props) => {
	const { generateLink = defultLinkGenerator, ...rest } = props;
	const { getAllFilters } = useFilters();

	const columns = useMemo(() => {
		return [
			{
				id: 'gorkoID',
				accessorFn: (data) => data.company?.gorkoId,
				emptyValue: '—',
				header: 'Gorko ID',
				renderType: CELL_RENDER_TYPE.id,
				filter: {
					code: 'companyGorkoId',
					valueType: VALUE_TYPE.number,
				},
			},
			{
				id: 'gorkoStatus',
				header: 'Статус',
				accessorFn: (data) => mapCompanyGorkoStatus(data.company),
				emptyValue: '—',
				minSize: COLUIMN_MIN_WIDTH.status,
				filter: {
					code: 'gorkoStatus',
					valueType: VALUE_TYPE.enum,
					payload: {
						enums: GORKO_STATUS_FILTER_OPTIONS,
					},
				},
			},
			{
				id: 'company',
				header: 'Название',
				accessorKey: 'company.name',
				minSize: COLUIMN_MIN_WIDTH.company,
				filter: {
					code: 'companyName',
					valueType: VALUE_TYPE.string,
				},
				cell: (props) => {
					const val = props.getValue();
					const link = generateLink({
						id: props.row.original.company.id,
					});
					return (
						<Link to={link} component={RouterLink}>
							{val}
						</Link>
					);
				},
			},
			{
				id: 'moment',
				header: 'Дата и время',
				accessorKey: 'createdAt',
				renderType: CELL_RENDER_TYPE.date,
				filter: {
					code: 'createdAt',
					valueType: VALUE_TYPE.date,
				},
			},
			{
				id: 'duration',
				header: 'Длительность',
				accessorKey: 'duration',
				durationUnit: (val) => {
					return (
						<FormattedMessage
							id="CellRender.duration.seconds"
							values={{ count: val }}
							defaultMessage="{count} сек"
						/>
					);
				},
				renderType: CELL_RENDER_TYPE.duration,
				filter: {
					code: 'duration',
					valueType: VALUE_TYPE.number,
				},
			},
			{
				id: 'type',
				header: 'Тип',
				accessorKey: 'type',
				filter: {
					code: 'callType',
					valueType: VALUE_TYPE.string,
				},
			},
			{
				id: 'manager',
				header: 'Менеджер',
				accessorKey: 'createdBy.name',
				minSize: COLUIMN_MIN_WIDTH.manager,
				filter: {
					code: 'createdByName',
					valueType: VALUE_TYPE.string,
				},
			},
			{
				id: 'from',
				header: 'С номера',
				accessorKey: 'from',
				minSize: COLUIMN_MIN_WIDTH.phone,
				filter: {
					code: 'from',
					valueType: VALUE_TYPE.string,
				},
			},
			{
				id: 'to',
				header: 'На номер',
				accessorKey: 'to',
				minSize: COLUIMN_MIN_WIDTH.phone,
				filter: {
					code: 'to',
					valueType: VALUE_TYPE.string,
				},
			},
			{
				id: 'tag',
				header: 'Тег',
				accessorKey: 'tag.label',
				filter: {
					code: 'tag',
					valueType: VALUE_TYPE.string,
				},
			},
			{
				id: 'record',
				header: 'Запись',
				accessorKey: 'record',
				filter: {
					code: 'record',
					valueType: VALUE_TYPE.string,
				},
				cell: () => {
					return (
						<Stack direction="row" gap={0.5}>
							<PlayCircleOutlined />
						</Stack>
					);
				},
			},
		];
	}, [generateLink]);

	const columnsByCode = useMemo(() => indexColumnsByCode(columns), [columns]);

	const selectedFilters = useMemo(() => {
		return mapSelectedFilters(getAllFilters(), columnsByCode, (code) => {
			if (code === 'gorkoStatus') {
				return getGorkoStatusFilterLabel;
			}

			return null;
		});
	}, [columnsByCode, getAllFilters]);

	return (
		<DefaultTable
			columns={columns}
			selectedFilters={selectedFilters}
			FilterPlateRP={FilterPlateCDefault}
			{...rest}
		/>
	);
};

DefaultTableCCalls.propTypes = {
	...DefaultTable.propTypes,
};

export default DefaultTableCCalls;
